import React, { useCallback, useEffect, useState } from "react";

import { Button, Stack } from "@mui/material";
import { AsyncContentMui, HorizontalNavBar, Search } from "components";
import { Create, Drawer, Table, Update } from "components/Characteristics";
import { useActionHook, useModals } from "hooks";
import { useDealActions } from "store";
import { Characteristic } from "store/types";

type CharacteristicsTabKey = "deal" | "client";

export const Characteristics: React.FC = () => {
  const [characteristic, setCharacteristic] = useState<Characteristic | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<CharacteristicsTabKey>("deal");

  const { deleteModal, errorModal } = useModals();

  const { deleteCharacteristicAction, getCharacteristicsAction } = useDealActions();

  const { action, data, loading, setData } = useActionHook(getCharacteristicsAction);

  // Client and Deal characteristics share the same API — "client" scopes the list, "deal" is the default.
  useEffect(() => {
    action(activeTab === "client" ? { entity_type: "client" } : undefined);
  }, [activeTab]);

  const { action: deleteCharacteristic } = useActionHook(deleteCharacteristicAction, {
    onSuccess: (id: number) => {
      setData((prevState) => {
        if (!prevState) return prevState;
        return prevState.filter((char) => char.id !== id);
      });
    },
    onReject: (error) => {
      const apiError = Object.keys(error.errors).length ? error.errors.error.join(", ") : error.message;
      errorModal({ msg: apiError || "Something went wrong" });
    }
  });

  const addNewCharacteristic = useCallback(
    (newCharacteristic: Characteristic) => {
      setData((prevState) => {
        if (!prevState) return prevState;
        return [newCharacteristic, ...prevState];
      });
      setIsCreate(false);
    },
    [setData]
  );
  const replaceCharacteristic = useCallback(
    (updatedCharacteristic: Characteristic) => {
      setData((prevState) => {
        if (!prevState) return prevState;
        return prevState.map((char) => (char.id === updatedCharacteristic.id ? updatedCharacteristic : char));
      });
      setCharacteristic(null);
    },
    [setData]
  );

  const handleDelete = useCallback(
    (id: number) => {
      deleteModal({
        action: async () => {
          try {
            deleteCharacteristic(id);
          } catch (e) {
            console.log(e);
          }
        },
        message: "Are you sure you want to archive characteristic?",
        element: "Archive Characteristic",
        onPressSuccess: "Archive"
      });
    },
    [deleteModal]
  );

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <Stack sx={{ bgcolor: "#f6f8fc", flexGrow: 1, minHeight: 0, gap: "16px" }}>
      <HorizontalNavBar
        value={activeTab}
        onChange={(_event, value) => {
          setActiveTab(value as CharacteristicsTabKey);
          setSearchValue("");
          setIsCreate(false);
          setCharacteristic(null);
        }}
        sx={{ minHeight: "25px", maxHeight: "25px", flexShrink: 0 }}
        tabs={[
          { value: "deal", label: "Deal characteristics" },
          { value: "client", label: "Client characteristics" }
        ]}
      />

      <Stack sx={{ gap: "16px", flexGrow: 1, minHeight: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Search value={searchValue} onChange={handleChange} placeholder="Search" />
          <Button variant="contained" onClick={() => setIsCreate(true)}>
            Add Characteristics
          </Button>
        </Stack>
        <AsyncContentMui isLoading={loading} spinnerProps={{ size: 100 }}>
          {data && (
            <Table
              setCharacteristic={setCharacteristic}
              data={
                searchValue ? data.filter((row) => row.name.toLowerCase().includes(searchValue.toLowerCase())) : data
              }
              onDelete={handleDelete}
            />
          )}
        </AsyncContentMui>
        <Drawer title="Add Characteristics" open={isCreate} onClose={() => setIsCreate(false)}>
          <Create onSuccess={addNewCharacteristic} onCancel={() => setIsCreate(false)} entityType={activeTab} />
        </Drawer>
        <Drawer title="Edit Characteristic" open={Boolean(characteristic)} onClose={() => setCharacteristic(null)}>
          <Update
            onSuccess={replaceCharacteristic}
            data={characteristic}
            onCancel={() => setCharacteristic(null)}
            entityType={activeTab}
          />
        </Drawer>
      </Stack>
    </Stack>
  );
};
